/**
 * Three.js renderer hosting the Bioclast scene.
 *
 * Public API mirrors the old WebGL2 renderer so `main.ts` and `controls.ts`
 * keep working: `setFeatures`, `start`, `stop`, `params`, `frameMs`,
 * `captureStream`, `snapshot`.
 */

import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";

import { AudioFeatures, ZERO_FEATURES } from "../audio/types";
import { BioclastScene, BioclastParams, DEFAULT_BIOCLAST } from "./scenes/bioclast";
import { FeedbackPass } from "./feedbackPass";
import { PostFxPass } from "./postFxPass";
import { GodRaysPass } from "./godRaysPass";
import {
  AltSceneApi,
  AltSceneType,
  createAltScene,
} from "./altScenes";
import { applyModulations, ModulationMap } from "./modulations";

export type SceneType = "bioclast" | AltSceneType;

export type RendererParams = BioclastParams;

export class Renderer {
  private gl: THREE.WebGLRenderer;
  private scene: BioclastScene;
  private rafId: number | null = null;
  private features: AudioFeatures = { ...ZERO_FEATURES };
  private hasSmoothedFeatures = false;
  private lastFeatureAt = performance.now();

  // Post-processing pipeline (composer renders scene → bloom → feedback → output).
  private composer: EffectComposer;
  private renderPass: RenderPass;
  private bloomPass: UnrealBloomPass;
  private feedbackPass: FeedbackPass;
  private postFxPass: PostFxPass;
  private godRaysPass: GodRaysPass;
  private outputPass: OutputPass;

  /** Look + reactivity parameters. UI mutates this directly. */
  public params: RendererParams = { ...DEFAULT_BIOCLAST };

  /** EMA frame time in ms — UI / adaptive quality can read this. */
  public frameMs = 16.7;

  /** Active scene type. Defaults to the legacy Bioclast galaxy. */
  public sceneType: SceneType = "bioclast";
  private altScene: AltSceneApi | null = null;
  private altSceneType: AltSceneType | null = null;
  private altCamBasePos: THREE.Vector3 | null = null;
  private altCamBaseFov: number | null = null;
  private altCamElapsed = 0;
  private readonly altCamTarget = new THREE.Vector3();
  private readonly altCamDesired = new THREE.Vector3();
  /** Optional per-preset framing override (vizz.fm cameraState). */
  private altCamOverride: { position: THREE.Vector3; target: THREE.Vector3; quaternion: THREE.Quaternion } | null = null;

  /** UI hook for vizz.fm-style camera framing state changes. */
  public onAltCameraOverrideChange: ((locked: boolean) => void) | null = null;

  /** Modulation routing map (vizz.fm-style). */
  public modulations: ModulationMap = {};
  /** Snapshot of the original (pre-modulated) param values, restored each frame. */
  private paramSnapshot: Partial<BioclastParams> = {};

  private lastFrameAt = performance.now();
  private startedAt = performance.now();
  private dprCap = 2;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.gl = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      alpha: true,
      premultipliedAlpha: false,
      preserveDrawingBuffer: true, // needed for snapshot()
      powerPreference: "high-performance",
    });
    this.gl.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.dprCap));
    this.gl.setClearColor(0x000000, 0);
    this.gl.outputColorSpace = THREE.SRGBColorSpace;

    const w = canvas.clientWidth || window.innerWidth;
    const h = canvas.clientHeight || window.innerHeight;
    this.scene = new BioclastScene(w, h);
    this.scene.params = this.params;

    // ---- Post-processing pipeline ----
    // RenderPass → UnrealBloomPass (selective glow on bright pixels) → OutputPass.
    // OutputPass handles tonemapping + sRGB conversion since EffectComposer's
    // intermediate render targets are linear-space.
    this.composer = new EffectComposer(this.gl);
    this.renderPass = new RenderPass(this.scene.scene, this.scene.camera);
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(w, h),
      this.params.bloomStrength,
      this.params.bloomRadius,
      this.params.bloomThreshold,
    );
    // Feedback (frame-history trail effect) sits AFTER bloom so trails
    // inherit the glow. With strength=0 the pass is essentially a
    // passthrough — costs ~one extra fullscreen draw per frame.
    this.feedbackPass = new FeedbackPass(w, h);
    this.outputPass = new OutputPass();
    this.postFxPass = new PostFxPass(w, h);
    this.godRaysPass = new GodRaysPass(w, h);
    this.composer.addPass(this.renderPass);
    this.composer.addPass(this.bloomPass);
    this.composer.addPass(this.feedbackPass);
    // God rays sample the bloomed + trailed image, then post-FX stylizes
    // the rays. Disabled by default (strength=0) so cost is paid only
    // when a scene/UI dials it up.
    this.composer.addPass(this.godRaysPass);
    // Post-FX stack runs AFTER bloom + feedback so distortions/CRT/grain
    // overlay the glow + trails like a finishing filter.
    this.composer.addPass(this.postFxPass);
    this.composer.addPass(this.outputPass);

    this.resize();
    window.addEventListener("resize", () => this.resize(), { passive: true });

    this.attachPointerControls();
  }

  /**
   * Mouse / touch drag rotates the camera; horizontal drag changes
   * `cameraOrbit` (yaw), vertical drag changes `cameraTilt`. Mouse wheel
   * adjusts `cameraZoom`. Two-finger pinch on touch devices also zooms.
   * All three knobs are also exposed as sliders, so users can pick whichever
   * feels better.
   */
  private attachPointerControls(): void {
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    const canvas = this.canvas;
    canvas.style.cursor = "grab";

    // Track active pointers so we can recognize a 2-finger pinch.
    const activePointers = new Map<number, { x: number; y: number }>();
    let pinchStartDist = 0;
    let pinchStartZoom = 1;

    const distanceBetweenPointers = (): number => {
      const pts = [...activePointers.values()];
      if (pts.length < 2) return 0;
      return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
    };

    const onDown = (ev: PointerEvent): void => {
      activePointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      try {
        canvas.setPointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
      if (activePointers.size === 1) {
        dragging = true;
        lastX = ev.clientX;
        lastY = ev.clientY;
        canvas.style.cursor = "grabbing";
      } else if (activePointers.size === 2) {
        // Switch from drag to pinch — capture the starting distance + zoom
        // so we can scale relative to it.
        dragging = false;
        pinchStartDist = distanceBetweenPointers();
        pinchStartZoom = this.params.cameraZoom;
        canvas.style.cursor = "ew-resize";
      }
    };
    const onMove = (ev: PointerEvent): void => {
      if (!activePointers.has(ev.pointerId)) return;
      activePointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

      if (activePointers.size >= 2 && pinchStartDist > 0) {
        // Pinch zoom — scale cameraZoom by the ratio of current to start
        // distances so the gesture feels physical.
        const dist = distanceBetweenPointers();
        const ratio = dist / pinchStartDist;
        this.params.cameraZoom = Math.max(
          0.15,
          Math.min(3.5, pinchStartZoom * ratio),
        );
        return;
      }

      if (!dragging) return;
      const dx = ev.clientX - lastX;
      const dy = ev.clientY - lastY;
      lastX = ev.clientX;
      lastY = ev.clientY;
      this.params.cameraOrbit += dx * 0.005;
      this.params.cameraTilt = Math.max(
        0,
        Math.min(1, this.params.cameraTilt - dy * 0.003),
      );
      // Any user drag releases the locked vizz cameraState so the user can
      // freely orbit/tilt instead of being snapped back each frame.
      if (this.altCamOverride) this.clearAltCameraOverride();
    };
    const onUp = (ev: PointerEvent): void => {
      activePointers.delete(ev.pointerId);
      try {
        canvas.releasePointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
      if (activePointers.size === 0) {
        dragging = false;
        canvas.style.cursor = "grab";
      } else if (activePointers.size === 1) {
        // Returning to single-finger drag — re-anchor lastX/Y from the
        // remaining pointer so we don't jump.
        const remaining = [...activePointers.values()][0];
        lastX = remaining.x;
        lastY = remaining.y;
        dragging = true;
        pinchStartDist = 0;
        canvas.style.cursor = "grabbing";
      }
    };
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointercancel", onUp);

    canvas.addEventListener(
      "wheel",
      (ev: WheelEvent) => {
        ev.preventDefault();
        // deltaY negative = scroll up = zoom in.
        const factor = Math.exp(-ev.deltaY * 0.0015);
        this.params.cameraZoom = Math.max(
          0.15,
          Math.min(3.5, this.params.cameraZoom * factor),
        );
        // Releases vizz framing lock, same as drag.
        if (this.altCamOverride) this.clearAltCameraOverride();
      },
      { passive: false },
    );
  }

  private resize(): void {
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.gl.setSize(w, h, false);
    this.scene.setSize(w, h);
    this.altScene?.setSize(w, h);
    if (this.composer) {
      this.composer.setSize(w, h);
      this.bloomPass.setSize(w, h);
      this.feedbackPass.setSize(w, h);
      this.postFxPass.setSize(w, h);
      this.godRaysPass.setSize(w, h);
    }
  }

  private adaptQuality(): void {
    if (this.frameMs > 30 && this.dprCap > 1) {
      this.dprCap = 1;
      this.gl.setPixelRatio(1);
      this.resize();
    }
  }

  setFeatures(f: AudioFeatures): void {
    const now = performance.now();
    const dtSec = Math.min(0.05, Math.max(0, (now - this.lastFeatureAt) / 1000));
    this.lastFeatureAt = now;

    if (!this.hasSmoothedFeatures) {
      this.features = { ...f };
      this.hasSmoothedFeatures = true;
    } else {
      const smoothBand = (previous: number, target: number): number => {
        const clampedTarget = THREE.MathUtils.clamp(Math.abs(target) < 0.002 ? 0 : target, 0, 1);
        const speed = clampedTarget > previous ? 18 : 7;
        return previous + (clampedTarget - previous) * (1 - Math.exp(-speed * dtSec));
      };
      const bass = smoothBand(this.features.bass, f.bass);
      const mid = smoothBand(this.features.mid, f.mid);
      const treble = smoothBand(this.features.treble, f.treble);
      const level = (bass + mid + treble) / 3;
      const beat = Math.max(f.beat, this.features.beat * Math.exp(-8 * dtSec));
      this.features = {
        ...f,
        bass,
        mid,
        treble,
        level,
        beat,
      };
    }
    this.scene.setFeatures(this.features);
  }

  start(): void {
    if (this.rafId !== null) return;
    const loop = (): void => {
      this.rafId = requestAnimationFrame(loop);
      this.draw();
    };
    this.rafId = requestAnimationFrame(loop);
  }

  stop(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  /** Apply (or clear) a vizz.fm-style cameraState override for the next alt scene update. */
  setAltCameraOverride(
    cs: { position: [number, number, number]; target: [number, number, number]; quaternion: [number, number, number, number] } | null,
  ): void {
    if (!cs) {
      this.clearAltCameraOverride();
      return;
    }
    this.altCamOverride = {
      position: new THREE.Vector3(cs.position[0], cs.position[1], cs.position[2]),
      target: new THREE.Vector3(cs.target[0], cs.target[1], cs.target[2]),
      quaternion: new THREE.Quaternion(cs.quaternion[0], cs.quaternion[1], cs.quaternion[2], cs.quaternion[3]),
    };
    // If an alt scene is already live, snap its base + camera now so the
    // change is visible immediately (without forcing a scene rebuild).
    if (this.altScene && this.altCamBasePos) {
      this.altCamBasePos.copy(this.altCamOverride.position);
      this.altScene.camera.position.copy(this.altCamOverride.position);
      this.altScene.camera.quaternion.copy(this.altCamOverride.quaternion);
      this.altCamElapsed = 0;
    }
    this.onAltCameraOverrideChange?.(true);
  }

  /** Drop any active vizz cameraState lock and let the standard rig take over. */
  clearAltCameraOverride(): void {
    const wasLocked = this.altCamOverride !== null;
    this.altCamOverride = null;
    this.altCamElapsed = 0;
    if (wasLocked) this.onAltCameraOverrideChange?.(false);
  }

  private updateAltSceneCamera(dt: number, motionT: number): void {
    if (!this.altScene || !this.altCamBasePos || this.altCamBaseFov === null) return;
    const cam = this.altScene.camera;
    const p = this.params;
    const f = this.features;
    this.altCamElapsed += dt;
    const t = this.altCamElapsed;
    const personality = this.altScene.cameraPersonality;
      const cameraMotionOn =
        p.cameraSpin > 0 ||
        p.camAutoRotateH !== 0 ||
        p.camAutoRotateV !== 0 ||
        p.camAutoRoll !== 0 ||
        p.camPanAmount > 0 ||
        p.camZoomAmount > 0 ||
        p.camFovAmount > 0;
      const motionScale = cameraMotionOn ? 1 : 0;

    const base = this.altCamBasePos;
    const baseRadius = Math.max(2, Math.hypot(base.x, base.z));
      const beatLift = motionScale * this.beatLevel * (0.8 + p.bassGravity * 0.2);
    const fovOsc =
        cameraMotionOn && p.camFovAmount > 0 && p.camFovPeriod > 0
        ? Math.sin((motionT / p.camFovPeriod) * Math.PI * 2) * p.camFovAmount
        : 0;

    if (personality === "staticParallax") {
      // Preserve scene-author framing. Add only gentle drift/bob and FOV breathing.
        const driftX = motionScale * Math.sin(t * 0.18) * (0.06 + p.camPanAmount * 0.8);
        const driftY = motionScale * Math.sin(t * 0.24) * (0.04 + p.camPanAmount * 0.5);
        const bob = motionScale * Math.sin(t * 0.38) * (0.025 + beatLift * 0.06);
      this.altCamDesired.set(base.x + driftX, base.y + driftY + bob, base.z);
      cam.position.lerp(this.altCamDesired, 0.035);
      this.altCamTarget.set(driftX * 0.4, driftY * 0.4, 0);
      cam.lookAt(this.altCamTarget);
    } else if (personality === "hallwayFly") {
      // Forward dolly with sway. Camera stays roughly where the scene placed
      // it, but oscillates along Z for the corridor feel + small lateral sway.
      const dollyZ =
          motionScale * Math.sin(t * (0.18 + p.flythroughSpeed * 0.18)) * (0.55 + p.camZoomAmount * 2.2);
        const sway = motionScale * Math.sin(t * 0.28) * (0.16 + p.camPanAmount * 0.9);
        const lift = motionScale * (Math.sin(t * 0.2) * 0.12 + beatLift * 0.08);
      this.altCamDesired.set(base.x + sway, base.y + lift, base.z + dollyZ);
      cam.position.lerp(this.altCamDesired, 0.04);
      this.altCamTarget.set(sway * 0.3, lift * 0.3, 0);
      cam.lookAt(this.altCamTarget);
    } else if (this.altCamOverride) {
      // Authored vizz framing: orbit GENTLY around the saved target, with the
      // saved position as the radius/elevation reference. This preserves the
      // unique cinematic angle of each preset while still feeling alive.
      const ov = this.altCamOverride;
      const offX = base.x - ov.target.x;
      const offY = base.y - ov.target.y;
      const offZ = base.z - ov.target.z;
      const radius = Math.max(0.5, Math.hypot(offX, offZ));
      const baseAzimuth = Math.atan2(offZ, offX);
        const spinRate = motionScale * (0.018 + p.cameraSpin * 0.06 + p.camAutoRotateH * 3);
      const azimuth = baseAzimuth + t * spinRate;
        const bob = motionScale * Math.sin(t * 0.24) * (0.025 + beatLift * 0.04);
      const zoomOsc =
          cameraMotionOn && p.camZoomAmount > 0 && p.camZoomPeriod > 0
          ? Math.sin((motionT / p.camZoomPeriod) * Math.PI * 2) * p.camZoomAmount * 0.25
          : 0;
      const r = radius * Math.max(0.7, 1 - (p.cameraZoom - 1) * 0.25 + zoomOsc);
      this.altCamTarget.copy(ov.target);
      this.altCamDesired.set(
        ov.target.x + Math.cos(azimuth) * r,
        ov.target.y + offY + bob,
        ov.target.z + Math.sin(azimuth) * r,
      );
      cam.position.lerp(this.altCamDesired, 0.04);
      cam.lookAt(this.altCamTarget);
    } else {
      // "orbit" or "closeOrbit" — full orbital rig.
      const close = personality === "closeOrbit";
      const spinRate =
          motionScale * ((close ? 0.08 : 0.025) + p.cameraSpin * (close ? 0.18 : 0.12) + p.camAutoRotateH * 4);
      const azimuth = p.cameraOrbit + t * spinRate;
      const tiltBias = (p.cameraTilt - 0.5) * (close ? 0.6 : 0.9);
        const bob = motionScale * Math.sin(t * (0.22 + p.camAutoRotateV * 3)) * (0.08 + p.camPanAmount * 0.4);
      const zoomOsc =
          cameraMotionOn && p.camZoomAmount > 0 && p.camZoomPeriod > 0
          ? Math.sin((motionT / p.camZoomPeriod) * Math.PI * 2) * p.camZoomAmount * 0.18
          : 0;
      const radiusScale = THREE.MathUtils.clamp(
        (close ? 0.85 : 1) / Math.max(0.15, p.cameraZoom) + zoomOsc,
        close ? 0.5 : 0.32,
        close ? 4.5 : 6.0,
      );
      const radius = baseRadius * radiusScale;
      const verticalRadius = Math.max(0.5, radius * (close ? 0.18 : 0.25));
      const targetJitter = motionScale * ((close ? 0.02 : 0.045) + p.camPanAmount * (close ? 0.35 : 0.7));
      this.altCamTarget.set(
        Math.sin(t * 0.21) * targetJitter,
        Math.sin(t * 0.17) * targetJitter * 0.5,
        Math.cos(t * 0.19) * targetJitter,
      );
      const polar = Math.PI / 2 - tiltBias;
      const y =
        base.y +
        Math.cos(polar) * verticalRadius +
        bob +
        beatLift * f.bass * 0.12;
      const horiz = Math.max(1, Math.sin(polar) * radius);
      const x = this.altCamTarget.x + Math.cos(azimuth) * horiz;
      const z = this.altCamTarget.z + Math.sin(azimuth) * horiz;
      this.altCamDesired.set(x, y, z);
      cam.position.lerp(this.altCamDesired, close ? 0.055 : 0.04);
      cam.lookAt(this.altCamTarget);
    }

    const nextFov = THREE.MathUtils.clamp(this.altCamBaseFov + fovOsc, 30, 95);
    if (Math.abs(nextFov - cam.fov) > 0.001) {
      cam.fov = nextFov;
      cam.updateProjectionMatrix();
    }
  }

  private draw(): void {
    const now = performance.now();
    const dt = Math.min(0.05, (now - this.lastFrameAt) / 1000);
    this.lastFrameAt = now;
    this.frameMs = this.frameMs * 0.92 + dt * 1000 * 0.08;
    this.adaptQuality();
    const motionT = (now - this.startedAt) / 1000;

    // Keep features in sync (no-op if already set this frame).
    this.scene.setFeatures(this.features);
    this.scene.params = this.params;

    // Ensure the right alt scene exists when a non-bioclast type is active.
    if (this.sceneType !== "bioclast" && this.sceneType !== this.altSceneType) {
      this.altScene?.dispose();
      const w = this.canvas.clientWidth || window.innerWidth;
      const h = this.canvas.clientHeight || window.innerHeight;
      this.altScene = createAltScene(this.sceneType as AltSceneType, w, h);
      this.altSceneType = this.sceneType as AltSceneType;
      this.altCamBasePos = this.altScene.camera.position.clone();
      this.altCamBaseFov = this.altScene.camera.fov;
      this.altCamElapsed = 0;
      // If a preset has supplied a vizz cameraState, snap the base to it so
      // the orbital rig pivots around the authored framing instead of (0,0,0).
      if (this.altCamOverride) {
        this.altCamBasePos.copy(this.altCamOverride.position);
        this.altScene.camera.position.copy(this.altCamOverride.position);
        this.altScene.camera.quaternion.copy(this.altCamOverride.quaternion);
      }
      // Wire the new alt scene into the renderPass for the post-fx chain.
      this.renderPass.scene = this.altScene.scene;
      this.renderPass.camera = this.altScene.camera;
    } else if (this.sceneType === "bioclast" && this.altSceneType !== null) {
      // Switching back to bioclast.
      this.altScene?.dispose();
      this.altScene = null;
      this.altSceneType = null;
      this.altCamBasePos = null;
      this.altCamBaseFov = null;
      this.altCamElapsed = 0;
      this.renderPass.scene = this.scene.scene;
      this.renderPass.camera = this.scene.camera;
    }

    // Apply modulations BEFORE updating the active scene. Snapshot then
    // restore so the user's slider values aren't permanently overwritten.
    let hadMods = false;
    for (const k of Object.keys(this.modulations) as (keyof BioclastParams)[]) {
      const m = this.modulations[k];
      if (m && m.enabled && m.amount > 0) {
        const v = (this.params as unknown as Record<string, unknown>)[k as string];
        if (typeof v === "number") {
          this.paramSnapshot[k] = v as never;
          hadMods = true;
        }
      }
    }
    if (hadMods) {
      applyModulations(this.params, this.modulations, this.features, motionT);
    }

    if (this.altScene) {
      this.altScene.update(dt, this.params, this.features);
    } else {
      this.scene.update(dt);
    }

    // Restore snapshot so the next frame starts from user-set values.
    if (hadMods) {
      for (const k of Object.keys(this.paramSnapshot) as (keyof BioclastParams)[]) {
        const v = this.paramSnapshot[k];
        if (typeof v === "number") {
          (this.params as unknown as Record<string, number>)[k as string] = v;
        }
      }
      this.paramSnapshot = {};
    }

    if (this.altScene) {
      this.updateAltSceneCamera(dt, motionT);
    }

    // Apply programmatic camera motion (vizz.fm-style auto-cam) AFTER the
    // scene places the camera. These layer on top of orbit/flythrough modes
    // without fighting them — we just add offsets to the already-positioned
    // camera. Skip the entire block when nothing is enabled.
    const cam = this.altScene ? this.altScene.camera : this.scene.camera;
    const p = this.params;
    if (!this.altScene) {
      if (
        p.camAutoRotateH !== 0 ||
        p.camAutoRotateV !== 0 ||
        p.camAutoRoll !== 0
      ) {
        cam.rotation.y += p.camAutoRotateH * dt;
        cam.rotation.x += p.camAutoRotateV * dt;
        cam.rotation.z += p.camAutoRoll * dt;
      }
      if (p.camFovAmount > 0 && p.camFovPeriod > 0) {
        const phase = (motionT / p.camFovPeriod) * Math.PI * 2;
          const nextFov = THREE.MathUtils.clamp(cam.fov + Math.sin(phase) * p.camFovAmount, 30, 95);
          if (Math.abs(nextFov - cam.fov) > 0.001) {
            cam.fov = nextFov;
            cam.updateProjectionMatrix();
          }
      }
      if (p.camZoomAmount > 0 && p.camZoomPeriod > 0) {
        // Mild dolly along camera forward vector — 1 unit world max.
        const phase = (motionT / p.camZoomPeriod) * Math.PI * 2;
        const dolly = Math.sin(phase) * p.camZoomAmount;
        cam.translateZ(-dolly);
      }
      if (p.camPanAmount > 0 && p.camPanPeriod > 0) {
        const phase = (motionT / p.camPanPeriod) * Math.PI * 2;
        cam.translateX(Math.sin(phase) * p.camPanAmount);
      }
    }

    // Sync bloom + feedback params each frame (cheap; lets the UI
    // mutate live). When BOTH bloom AND feedback are off, skip the
    // composer entirely and render the scene directly to the canvas
    // for max perf — otherwise route through the composer chain.
    const fxActive = this.postFxPass.isAnyEffectActive();
    const raysActive = this.params.godRaysEnabled && this.params.godRaysStrength > 0.001;
    const useComposer =
      this.params.bloomEnabled || this.params.feedbackStrength > 0 || fxActive || raysActive;
    if (useComposer) {
      this.bloomPass.enabled = this.params.bloomEnabled;
      this.bloomPass.strength = this.params.bloomStrength;
      this.bloomPass.radius = this.params.bloomRadius;
      this.bloomPass.threshold = this.params.bloomThreshold;
      // Push live feedback knobs into the pass.
      this.feedbackPass.params.strength = this.params.feedbackStrength;
      this.feedbackPass.params.zoom = this.params.feedbackZoom;
      this.feedbackPass.params.rotate = this.params.feedbackRotate;
      this.feedbackPass.params.driftX = this.params.feedbackDriftX;
      this.feedbackPass.params.driftY = this.params.feedbackDriftY;
      this.feedbackPass.params.decay = this.params.feedbackDecay;
      // Push post-FX knobs.
      const fp = this.postFxPass.params;
      fp.enabled = this.params.postFxEnabled;
      fp.rgbShift = this.params.fxRgbShift;
      fp.glitch = this.params.fxGlitch;
      fp.crt = this.params.fxCrt;
      fp.kaleidoscope = this.params.fxKaleidoscope;
      fp.pixelate = this.params.fxPixelate;
      fp.dither = this.params.fxDither;
      fp.dotScreen = this.params.fxDotScreen;
      fp.filmGrain = this.params.fxFilmGrain;
      fp.tiltShift = this.params.fxTiltShift;
      fp.radialBlur = this.params.fxRadialBlur;
      fp.vignette = this.params.fxVignette;
      fp.swirl = this.params.fxSwirl;
      fp.barrel = this.params.fxBarrel;
      fp.kaleidoscopeSegments = this.params.fxKaleidoscopeSegments;
      fp.gridTile = this.params.fxGridTile;
      fp.gridTileColumns = this.params.fxGridTileColumns;
      // Extended vizz-parity params.
      fp.rgbShiftAngle = this.params.fxRgbShiftAngle;
      fp.rgbShiftOpacity = this.params.fxRgbShiftOpacity;
      fp.glitchSpeed = this.params.fxGlitchSpeed;
      fp.glitchBlock = this.params.fxGlitchBlock;
      fp.glitchOpacity = this.params.fxGlitchOpacity;
      fp.crtCurvature = this.params.fxCrtCurvature;
      fp.crtScanlineIntensity = this.params.fxCrtScanlineIntensity;
      fp.crtRgbOffset = this.params.fxCrtRgbOffset;
      fp.crtOpacity = this.params.fxCrtOpacity;
      fp.kaleidoscopeRotation = this.params.fxKaleidoscopeRotation;
      fp.kaleidoscopeCenterX = this.params.fxKaleidoscopeCenterX;
      fp.kaleidoscopeCenterY = this.params.fxKaleidoscopeCenterY;
      fp.kaleidoscopeOpacity = this.params.fxKaleidoscopeOpacity;
      fp.pixelateOpacity = this.params.fxPixelateOpacity;
      fp.ditherLevels = this.params.fxDitherLevels;
      fp.ditherPixelScale = this.params.fxDitherPixelScale;
      fp.ditherOpacity = this.params.fxDitherOpacity;
      fp.dotScreenAngle = this.params.fxDotScreenAngle;
      fp.dotScreenCenterX = this.params.fxDotScreenCenterX;
      fp.dotScreenCenterY = this.params.fxDotScreenCenterY;
      fp.dotScreenOpacity = this.params.fxDotScreenOpacity;
      fp.filmGrainScanlineIntensity = this.params.fxFilmGrainScanlineIntensity;
      fp.filmGrainScanlineCount = this.params.fxFilmGrainScanlineCount;
      fp.filmGrainGrayscale = this.params.fxFilmGrainGrayscale;
      fp.filmGrainOpacity = this.params.fxFilmGrainOpacity;
      fp.tiltShiftFocusPos = this.params.fxTiltShiftFocusPos;
      fp.tiltShiftFocusWidth = this.params.fxTiltShiftFocusWidth;
      fp.tiltShiftFalloff = this.params.fxTiltShiftFalloff;
      fp.tiltShiftOpacity = this.params.fxTiltShiftOpacity;
      fp.radialBlurCenterX = this.params.fxRadialBlurCenterX;
      fp.radialBlurCenterY = this.params.fxRadialBlurCenterY;
      fp.radialBlurInnerRadius = this.params.fxRadialBlurInnerRadius;
      fp.radialBlurFalloff = this.params.fxRadialBlurFalloff;
      fp.radialBlurOpacity = this.params.fxRadialBlurOpacity;
      fp.vignetteSize = this.params.fxVignetteSize;
      fp.vignetteOpacity = this.params.fxVignetteOpacity;
      fp.swirlAngle = this.params.fxSwirlAngle;
      fp.swirlRadius = this.params.fxSwirlRadius;
      fp.swirlFalloff = this.params.fxSwirlFalloff;
      fp.swirlCenterX = this.params.fxSwirlCenterX;
      fp.swirlCenterY = this.params.fxSwirlCenterY;
      fp.swirlOpacity = this.params.fxSwirlOpacity;
      fp.barrelAmountX = this.params.fxBarrelAmountX;
      fp.barrelAmountY = this.params.fxBarrelAmountY;
      fp.barrelCenterX = this.params.fxBarrelCenterX;
      fp.barrelCenterY = this.params.fxBarrelCenterY;
      fp.barrelZoom = this.params.fxBarrelZoom;
      fp.barrelChroma = this.params.fxBarrelChroma;
      fp.barrelVignette = this.params.fxBarrelVignette;
      fp.barrelVignetteSize = this.params.fxBarrelVignetteSize;
      fp.barrelOpacity = this.params.fxBarrelOpacity;
      fp.gridTileRows = this.params.fxGridTileRows;
      fp.gridTileGap = this.params.fxGridTileGap;
      fp.gridTileMirror = this.params.fxGridTileMirror;
      fp.gridTileCenterX = this.params.fxGridTileCenterX;
      fp.gridTileCenterY = this.params.fxGridTileCenterY;
      fp.gridTileOpacity = this.params.fxGridTileOpacity;
      fp.sepia = this.params.fxSepia;
      fp.sepiaOpacity = this.params.fxSepiaOpacity;
      fp.bleach = this.params.fxBleach;
      fp.bleachOpacity = this.params.fxBleachOpacity;
      this.postFxPass.enabled = fxActive;
      this.postFxPass.syncUniforms(motionT, this.scene.beatLevel);

      // God rays — slow lissajous on the sun position when auto-orbit is on,
      // otherwise honor the manual centerX/centerY params verbatim.
      const gp = this.godRaysPass.params;
      gp.enabled = this.params.godRaysEnabled;
      gp.strength = this.params.godRaysStrength;
      gp.decay = this.params.godRaysDecay;
      gp.density = this.params.godRaysDensity;
      gp.weight = this.params.godRaysWeight;
      gp.samples = this.params.godRaysSamples;
      if (this.params.godRaysAutoOrbit) {
        gp.centerX = 0.5 + Math.sin(motionT) * 0.35;
        gp.centerY = 0.5 + Math.sin(motionT * 0.913) * 0.35;
      } else {
        gp.centerX = this.params.godRaysCenterX;
        gp.centerY = this.params.godRaysCenterY;
      }
      this.godRaysPass.enabled = raysActive;
      this.godRaysPass.syncUniforms(motionT);
      // GPGPU alt scenes must advance their simulation BEFORE the
      // composer binds its writeBuffer; otherwise their setRenderTarget
      // calls stomp the composer's FBO binding and the particles
      // render to the canvas backbuffer instead of the composer input.
      this.altScene?.prerender?.(this.gl, dt, (now - this.startedAt) / 1000);
      this.composer.render(dt);
    } else {
      this.altScene?.prerender?.(this.gl, dt, (now - this.startedAt) / 1000);
      const renderScene = this.altScene ? this.altScene.scene : this.scene.scene;
      const renderCam = this.altScene ? this.altScene.camera : this.scene.camera;
      this.gl.render(renderScene, renderCam);
    }
  }

  /** Latest backdrop tint computed by the active scene. */
  get backdrop(): { r: number; g: number; b: number; alpha: number; blurPx: number } {
    return this.altScene ? this.altScene.backdrop : this.scene.backdrop;
  }

  /** Latest beat envelope (0..1). Useful for beat-locked UI behaviors. */
  get beatLevel(): number {
    return this.altScene ? this.altScene.beatLevel : this.scene.beatLevel;
  }

  /** Returns the underlying canvas element (for compositors / DOM observers). */
  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  /** Returns a stream that mirrors the canvas at a given fps for recording. */
  captureStream(fps = 60): MediaStream {
    return this.canvas.captureStream(fps);
  }

  /** Snapshot the current frame as a PNG blob. */
  snapshot(): Promise<Blob | null> {
    return new Promise((resolve) => {
      this.canvas.toBlob((b) => resolve(b), "image/png");
    });
  }
}
